import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Phone, Search, User } from 'lucide-react';
import { contactInfo } from '../data/services';
import LocationSelector from './LocationSelector';
import { useServices } from '../context/ServiceContext';
import { useCustomerAuth } from '../context/CustomerAuthContext';

export const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { globalSearchQuery, setGlobalSearchQuery } = useServices();
  const { customerToken, customer } = useCustomerAuth();

  const navLinks = [
    { path: '/', label: 'Home' },
    { path: '/about', label: 'About Us' },
    { path: '/contact', label: 'Contact' },
  ];

  const isActive = (path) => location.pathname === path;

  const customerTarget = customerToken ? '/customer-account' : '/customer-login';
  const customerLabel = customerToken ? (customer?.name ? customer.name.split(' ')[0] : 'Account') : 'Sign In';

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 glass-effect border-b border-blue-200/50" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center" data-testid="navbar-logo">
            <img 
              src="/assets/logo.svg" 
              alt={contactInfo.brandName} 
              className="h-10 lg:h-12 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`nav-link text-sm font-medium transition-colors ${isActive(link.path) ? 'text-blue-600' : 'text-slate-600 hover:text-blue-600'
                  }`}
                data-testid={`nav-link-${link.label.toLowerCase().replace(' ', '-')}`}
              >
                {link.label}
              </Link>
            ))}

            {/* Customer Entry */}
            <Link
              to={customerTarget}
              className={`flex items-center gap-1.5 text-sm font-medium transition-colors ${isActive(customerTarget) ? 'text-blue-600' : 'text-slate-600 hover:text-blue-600'
                }`}
              data-testid="nav-link-customer-account"
            >
              <User size={16} />
              <span>{customerLabel}</span>
            </Link>
          </div>

          {/* CTA Button & Location */}
          <div className="hidden md:flex items-center gap-4">
            <LocationSelector />

            <div className="relative group w-48 lg:w-64">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Search className="h-4 w-4 text-slate-400 group-focus-within:text-blue-500 transition-colors" />
              </div>
              <input
                type="text"
                value={globalSearchQuery}
                onChange={(e) => {
                  setGlobalSearchQuery(e.target.value);
                  if (location.pathname !== '/') navigate('/');
                }}
                placeholder="Search services..."
                className="w-full pl-9 pr-4 py-2 bg-white/50 border border-blue-200 rounded-full text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all font-sans"
              />
            </div>

            <a
              href={`tel:+91${contactInfo.phone}`}
              className="flex items-center gap-2 bg-blue-500 hover:bg-blue-600 text-white px-5 py-2.5 rounded-full font-medium text-sm transition-colors"
              data-testid="navbar-call-btn"
            >
              <Phone size={16} />
              <span>Call Now</span>
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden p-2 text-slate-600 hover:text-blue-500"
            onClick={() => setIsOpen(!isOpen)}
            data-testid="mobile-menu-toggle"
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-white border-t border-gray-100"
            data-testid="mobile-menu"
          >
            <div className="px-4 py-4 space-y-4">
              <div className="flex justify-between items-center bg-gray-50 p-3 rounded-xl border border-gray-100">
                <span className="text-xs font-bold text-slate-500 uppercase tracking-wider">Your Location</span>
                <LocationSelector />
              </div>

              {/* Mobile Search */}
              <div className="relative group w-full">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Search className="h-4 w-4 text-slate-400 group-focus-within:text-blue-500 transition-colors" />
                </div>
                <input
                  type="text"
                  value={globalSearchQuery}
                  onChange={(e) => {
                    setGlobalSearchQuery(e.target.value);
                    if (location.pathname !== '/') {
                      navigate('/');
                      setIsOpen(false);
                    }
                  }}
                  placeholder="Search services..."
                  className="w-full pl-9 pr-4 py-3 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all font-sans"
                />
              </div>

              <div className="space-y-3">
                {navLinks.map((link) => (
                  <Link
                    key={link.path}
                    to={link.path}
                    className={`block py-2 px-3 rounded-lg text-sm font-medium ${isActive(link.path)
                        ? 'bg-blue-50 text-blue-500'
                        : 'text-slate-600 hover:bg-gray-50'
                      }`}
                    onClick={() => setIsOpen(false)}
                    data-testid={`mobile-nav-link-${link.label.toLowerCase().replace(' ', '-')}`}
                  >
                    {link.label}
                  </Link>
                ))}

                {/* Mobile Customer Entry */}
                <Link
                  to={customerTarget}
                  className={`flex items-center gap-2 py-2 px-3 rounded-lg text-sm font-medium ${isActive(customerTarget)
                      ? 'bg-blue-50 text-blue-500'
                      : 'text-slate-600 hover:bg-gray-50'
                    }`}
                  onClick={() => setIsOpen(false)}
                  data-testid="mobile-nav-link-customer-account"
                >
                  <User size={16} />
                  <span>{customerLabel}</span>
                </Link>

                <a
                  href={`tel:+91${contactInfo.phone}`}
                  className="flex items-center justify-center gap-2 bg-blue-500 text-white py-3 rounded-full font-medium text-sm mt-4"
                  data-testid="mobile-call-btn"
                >
                  <Phone size={16} />
                  <span>Call Now</span>
                </a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};
